// ============================================================
// Edata.js — All constants, mock data, and configuration
// ============================================================

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import WalletService from "../../../../services/walletService";

export const SITE_CONFIG = {
  name: "theVisaCo",
  tagline: "Stay Connected Everywhere",
  description: "Premium eSIM plans for seamless global connectivity",
};

// ─── Centralized UI Config ───────────────────────────────────
export const UI_CONFIG = {
  popularDestinations: {
    desktop: { cols: 4, visible: 8 },
    tablet: { cols: 3, visible: 6 },
    mobile: { cols: 2, visible: 4 },
    loadMore: 4,
  },
  browseCountries: {
    desktop: { cols: 4, visible: 8 },
    tablet: { cols: 3, visible: 6 },
    mobile: { cols: 2, visible: 4 },
    loadMore: 4,
  },
  reviews: {
    desktop: { cols: 3, visible: 3 },
    tablet: { cols: 2, visible: 4 },
    mobile: { cols: 1, visible: 2 },
    loadMore: 3,
  },
  faq: {
    desktop: { visible: 6 },
    tablet: { visible: 4 },
    mobile: { visible: 3 },
    loadMore: 3,
  },
  breakpoints: {
    tablet: 1024,
    mobile: 768,
  },
};

// ─── Per-method convenience fees (USD) ──────────────────────
export const PAYMENT_CONVENIENCE_FEES = {
  upi: 0,
  netbanking: 1.5,
  card: 2.5,
  wallet: 0,
};

export const TAX_RATE = 0.18; // 18%

export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
  { code: "hi", label: "हिन्दी" },
];

export const CURRENCIES = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "€", label: "Euro" },
  { code: "GBP", symbol: "£", label: "British Pound" },
  { code: "INR", symbol: "₹", label: "Indian Rupee" },
  { code: "AED", symbol: "د.إ", label: "UAE Dirham" },
  { code: "JPY", symbol: "¥", label: "Japanese Yen" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar" },
];

export const REGIONS = [];

export const COUNTRIES = [];

// Plan generator — creates dynamic plans per country
export const generatePlans = (countryId) => {
  const basePlans = [
    {
      id: `${countryId}-d1`,
      type: "data",
      label: "Starter",
      data: "1 GB",
      dataGB: 1,
      validity: 7,
      price: 4.99,
      features: ["1 GB Data", "7 Days Validity", "4G LTE Network", "Instant Activation"],
      unlimited: false,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-d2`,
      type: "data",
      label: "Explorer",
      data: "3 GB",
      dataGB: 3,
      validity: 15,
      price: 9.99,
      features: ["3 GB Data", "15 Days Validity", "4G LTE Network", "Instant Activation"],
      unlimited: false,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-d3`,
      type: "data",
      label: "Traveller",
      data: "5 GB",
      dataGB: 5,
      validity: 30,
      price: 14.99,
      features: ["5 GB Data", "30 Days Validity", "4G/5G Network", "Instant Activation", "Hotspot Enabled"],
      unlimited: false,
      hasVoice: false,
      hasSMS: false,
      popular: true,
    },
    {
      id: `${countryId}-d4`,
      type: "data",
      label: "Professional",
      data: "10 GB",
      dataGB: 10,
      validity: 30,
      price: 22.99,
      features: ["10 GB Data", "30 Days Validity", "4G/5G Network", "Instant Activation", "Hotspot Enabled"],
      unlimited: false,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-d5`,
      type: "data",
      label: "Extended",
      data: "20 GB",
      dataGB: 20,
      validity: 60,
      price: 34.99,
      features: ["20 GB Data", "60 Days Validity", "4G/5G Network", "Instant Activation", "Hotspot Enabled", "Priority Support"],
      unlimited: false,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-d6`,
      type: "data",
      label: "Long Stay",
      data: "50 GB",
      dataGB: 50,
      validity: 90,
      price: 54.99,
      features: ["50 GB Data", "90 Days Validity", "4G/5G Network", "Instant Activation", "Hotspot Enabled", "Priority Support"],
      unlimited: false,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-d7`,
      type: "data",
      label: "Annual",
      data: "100 GB",
      dataGB: 100,
      validity: 365,
      price: 89.99,
      features: ["100 GB Data", "365 Days Validity", "4G/5G Network", "Instant Activation", "Hotspot Enabled", "Priority Support", "Dedicated Support"],
      unlimited: false,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-u1`,
      type: "unlimited",
      label: "Unlimited Basic",
      data: "Unlimited",
      dataGB: Infinity,
      validity: 7,
      price: 12.99,
      features: ["Unlimited Data", "7 Days Validity", "4G LTE Network", "Instant Activation", "Fair Use: 2 GB/day at full speed"],
      unlimited: true,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-u2`,
      type: "unlimited",
      label: "Unlimited Plus",
      data: "Unlimited",
      dataGB: Infinity,
      validity: 30,
      price: 29.99,
      features: ["Unlimited Data", "30 Days Validity", "4G/5G Network", "Instant Activation", "Fair Use: 3 GB/day at full speed", "Hotspot Enabled"],
      unlimited: true,
      hasVoice: false,
      hasSMS: false,
      popular: true,
    },
    {
      id: `${countryId}-u3`,
      type: "unlimited",
      label: "Unlimited Pro",
      data: "Unlimited",
      dataGB: Infinity,
      validity: 90,
      price: 69.99,
      features: ["Unlimited Data", "90 Days Validity", "4G/5G Network", "Instant Activation", "Fair Use: 5 GB/day at full speed", "Hotspot Enabled", "Priority Network Access"],
      unlimited: true,
      hasVoice: false,
      hasSMS: false,
    },
    {
      id: `${countryId}-b1`,
      type: "bundle",
      label: "Talk & Text",
      data: "2 GB",
      dataGB: 2,
      validity: 15,
      price: 17.99,
      features: ["2 GB Data", "100 Minutes Voice", "100 SMS", "15 Days Validity", "4G LTE Network"],
      unlimited: false,
      hasVoice: true,
      hasSMS: true,
      voice: "100 min",
      sms: "100 SMS",
    },
    {
      id: `${countryId}-b2`,
      type: "bundle",
      label: "Complete",
      data: "5 GB",
      dataGB: 5,
      validity: 30,
      price: 27.99,
      features: ["5 GB Data", "300 Minutes Voice", "Unlimited SMS", "30 Days Validity", "4G/5G Network", "Hotspot Enabled"],
      unlimited: false,
      hasVoice: true,
      hasSMS: true,
      voice: "300 min",
      sms: "Unlimited SMS",
      popular: true,
    },
    {
      id: `${countryId}-b3`,
      type: "bundle",
      label: "Premium Bundle",
      data: "10 GB",
      dataGB: 10,
      validity: 30,
      price: 39.99,
      features: ["10 GB Data", "Unlimited Voice", "Unlimited SMS", "30 Days Validity", "4G/5G Network", "Hotspot Enabled", "Priority Support"],
      unlimited: false,
      hasVoice: true,
      hasSMS: true,
      voice: "Unlimited",
      sms: "Unlimited SMS",
    },
  ];
  return basePlans;
};

export const FAQ_ITEMS = [];

export const TERMS_AND_CONDITIONS = `
**TVC ESIM Terms & Conditions**

**1. Service Description**
TheVisaCo provides digital eSIM plans for international data connectivity. By purchasing a plan, you agree to these terms.

**2. Eligibility**
Your device must be unlocked and eSIM-compatible. You are responsible for verifying compatibility before purchase.

**3. Activation**
Plans are activated upon QR code scan. Validity periods begin from the moment of first use, not purchase date.

**4. Fair Use Policy**
Unlimited plans are subject to fair-use thresholds as stated per plan. Excessive usage may result in speed throttling.

**5. Refund Policy**
Unactivated plans are eligible for a full refund within 14 days of purchase. Activated plans are non-refundable unless a technical fault is confirmed by our support team.

**6. Data Privacy**
We collect minimal personal data (email address) solely for delivering your QR code and providing support. We do not sell or share your data with third parties.

**7. Liability**
TheVisaCo is not liable for network outages, coverage gaps in remote areas, or device incompatibilities. Service quality depends on local carrier infrastructure.

**8. Payment**
All prices are displayed in your selected currency. Payments are processed securely. Convenience fees vary by payment method and are disclosed before confirmation.

**9. Support**
24/7 support is available via email at. Response times may vary during peak periods.

**10. Governing Law**
These terms are governed by applicable international e-commerce regulations.

By proceeding with your purchase, you confirm that you have read, understood, and agree to these Terms & Conditions and our Privacy Policy.
`;

export const REVIEWS = [];

export const PAYMENT_METHODS = [
  { id: "upi", label: "UPI", description: "Pay via UPI ID or QR code — No convenience fee" },
  { id: "netbanking", label: "Net Banking", description: "All major banks supported — ₹1.50 convenience fee" },
  { id: "card", label: "Debit / Credit Card", description: "Visa, Mastercard, Amex, RuPay — ₹2.50 convenience fee" },
  { id: "wallet", label: "Pay with Reward Money", description: "Use your available reward balance — No convenience fee" },
];

export const BANKS = [
  "State Bank of India",
  "HDFC Bank",
  "ICICI Bank",
  "Axis Bank",
  "Kotak Mahindra Bank",
  "Punjab National Bank",
  "Bank of Baroda",
  "Canara Bank",
  "Union Bank of India",
  "IndusInd Bank",
];

export const EXCHANGE_RATES = {
  USD: 1, EUR: 0.92, GBP: 0.79, INR: 83.5, AED: 3.67, JPY: 151.2, AUD: 1.52, CAD: 1.36,
};



export const customRound = (num) => {
  const decimal = num - Math.floor(num);
  if (decimal > 0.5) {
    return Math.ceil(num);
  }
  return Math.floor(num);
};

export const convertPrice = (priceUSD, currencyCode) => {
  // const rate = EXCHANGE_RATES[currencyCode] || 1;
  // const symbol = CURRENCIES.find((c) => c.code === currencyCode)?.symbol || "$";
  // const converted = priceUSD * rate;
  // return `${symbol}${converted.toFixed(2)}`;
  
  // Always use INR fixed rate
  const converted = priceUSD * 83;
  return `₹${customRound(converted)}`;
};

export const getPlanTypeLabel = (type) => {
  switch (type) {
    case "data": return "Data Only";
    case "unlimited": return "Unlimited";
    case "bundle": return "Voice + SMS + Data";
    default: return type;
  }
};

// Search helper: matches name or alias
export const searchCountries = (query, regionFilter = "all") => {
  const q = query.toLowerCase().trim();
  return COUNTRIES.filter(c => {
    const regionMatch = regionFilter === "all" || c.region === regionFilter;
    if (!regionMatch) return false;
    if (!q) return true;
    const nameMatch = c.name.toLowerCase().includes(q);
    const aliasMatch = (c.aliases || []).some(a => a.includes(q));
    return nameMatch || aliasMatch;
  });
};

const getCountryNameFromCode = (code) => {
  if (!code) return "";
  const c = COUNTRIES.find(c => c.id === code || c.flag === code);
  return c ? c.name : code;
};

export const getCountryName = (countryValue) => {
  if (!countryValue) return "";
  const c = COUNTRIES.find(x => x.id === countryValue || x.flag === countryValue || x.name.toLowerCase() === countryValue.toLowerCase());
  return c ? c.name : countryValue;
};

// ─── eSIM Features Data ──────────────────────────────────────
export const FEATURES = [
  { icon: "⚡", title: "Instant Activation", desc: "Receive your QR code by email within minutes of purchase. Scan and connect." },
  { icon: "🔒", title: "No Hidden Fees", desc: "The price you see is the price you pay. No overage charges, no surprises." },
  { icon: "🌍", title: "Global Coverage", desc: "100+ countries, 300+ networks. We partner with the best carriers worldwide." },
  { icon: "💬", title: "24/7 Support", desc: "Our team is available around the clock to help with any connectivity issues." },
  { icon: "📱", title: "Dual SIM Ready", desc: "Keep your home number active while using theVisaCo data internationally." },
  { icon: "🛡", title: "Secure Checkout", desc: "Bank-level encryption on all transactions. Your data is always protected." },
];

export const MAX_REWARD_DISCOUNT_PCT = 0.20;

export const WHATSAPP_COUNTRY_CODES = [
  { value: "+1", label: "+1" },
  { value: "+33", label: "+33" },
  { value: "+44", label: "+44" },
  { value: "+49", label: "+49" },
  { value: "+61", label: "+61" },
  { value: "+65", label: "+65" },
  { value: "+81", label: "+81" },
  { value: "+91", label: "+91" },
  { value: "+971", label: "+971" },
];

export const PLAN_TYPE_FILTERS = [
  { value: "all", label: "All Plans" },
  { value: "data", label: "Data Only" },
  { value: "unlimited", label: "Unlimited" },
  { value: "bundle", label: "Voice + SMS + Data" },
];

export const SORT_OPTIONS = [
  { value: "popular", label: "All Popularity" },
  { value: "mostvisited", label: "Most Visited" },
  { value: "toppicks", label: "Top Picks" },
  { value: "trending", label: "Trending" },
  { value: "bestdeal", label: "Best Deal" },
  { value: "mostloved", label: "Most Loved" },
];

export const CHECKOUT_STEPS = ["Details", "Payment", "Review"];

export const ORDER_ID_PREFIX = "RML-";

export { generateReceiptHTML } from "../../../../receipt/receipt.js";

// ============================================================
// EsimContext — eSIM Context, Provider, Wallet Hooks
// ============================================================

const EsimContext = createContext();

export function EsimProvider({ children }) {
  const [esimState, setEsimState] = useState({
    cart: [],
    user: null,
    showAuth: false,
  });

  return (
    <EsimContext.Provider value={{ esimState, setEsimState }}>
      {children}
    </EsimContext.Provider>
  );
}

export function useEsim() {
  return useContext(EsimContext);
}

export function useEsimWallet() {
  const [walletBalance, setWalletBalance] = useState(0);

  const loadBalance = useCallback(async () => {
    try {
      const wallet = await WalletService.getWallet();
      const total = (wallet.redeemable?.balance ?? 0) + (wallet.nonRedeemable?.balance ?? 0);
      setWalletBalance(total);
    } catch {
      setWalletBalance(0);
    }
  }, []);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  return { walletBalance, refreshWallet: loadBalance };
}

export async function getRedeemableBalance(email) {
  if (!email) return 0;
  try {
    const wallet = await WalletService.getWallet();
    return wallet.redeemable?.balance ?? 0;
  } catch {
    return 0;
  }
}

export async function redeemWalletForEsim(totalAmount, userEmail) {
  if (!userEmail) return { success: false, message: "Please login to use wallet balance" };

  const maxDiscountAmount = totalAmount * 0.2;
  try {
    const wallet = await WalletService.getWallet();
    const availableBalance = wallet.redeemable?.balance ?? 0;
    const discount = Math.min(availableBalance, maxDiscountAmount);

    if (discount <= 0) return { success: false, message: "No wallet balance available" };

    return { success: true, discount };
  } catch {
    return { success: false, message: "Failed to fetch wallet balance" };
  }
}



